<template>
  <div :class="_fs_class">
    <slot></slot>

    <input
      :id="id"
      v-model="sel"
      type="checkbox"
      :name="id"
      :value="value"
      :disabled="readonly ? 'disabled' : null"
      :data-label="label"
      :aria-describedby="ariaDescribedByContent()"
      @change="_onChange"
      @focus="_onFocus"
    />
    <slot name="label">
      <cat-label
        class="checkbox-label"
        :label-id="id + '-lbl'"
        :for-id="id + ''"
        :label="label"
        :optional="!mandatory"
        :readonly="readonly"
        :display-optional="displayOptional"
        :display-required="displayRequired"
      ></cat-label>
    </slot>
    <cat-hint :id="id + '_hint'" :text="hint" />
    <cat-help
      v-if="help && help.text"
      :id="id + '_help'"
      :heading="help.title"
      :text="help.text"
      :aria-describedby="label && id ? id + '-lbl' : null"
    />
    <slot name="after-input"></slot>
    <cat-errors :errors="errors" />
  </div>
</template>

<script lang="ts">
import { defineComponent, inject } from "vue";
import { defaultResolveTxt } from "@/components/field-element";
import CatErrors from "@/components/catErrors/catErrors.vue";
import CatHint from "@/components/utilities/hint.vue";
import CatLabel from "@/components/utilities/cat-label.vue";
import CatHelp from "@/components/utilities/help.vue";

export default defineComponent({
  setup() {
    // Inject the global value
    const resolveTxt = inject("resolveTxt", defaultResolveTxt);
    const displayParams = inject("displayParams", false);

    return {
      resolveTxt,
      displayParams
    };
  },
  name: "CatCheckBox",
  components: {
    CatErrors,
    CatHint,
    CatHelp,
    CatLabel
  },
  props: {
    id: {
      type: [Number, String],
      description: "Checkbox ID plus '-lbl'."
    },
    checked: {
      type: Boolean,
      description: "Deprecated - Not in use"
    },
    errors: {
      type: Array,
      description: "Displays error level (1-4) and error message."
    },
    label: {
      type: String,
      description: "Adds label to checkbox."
    },
    ariaDescribedby: {
      type: String,
      default: null,
      description:
        "Sets the aria-describedby attribute for the checkbox. This is a unique ID for the hint text."
    },
    mandatory: {
      type: Boolean,
      description: "Makes checkbox mandatory."
    },
    selected: {
      type: Boolean,
      description: "Indicates the checkbox as ticked."
    },
    value: {
      type: String,
      default: null,
      description: "Adds a value to the checkbox that can be reused."
    },
    readonly: {
      type: Boolean,
      description: "Disables checkbox (eg for reviewing)."
    },
    size: {
      type: String,
      default: "full",
      description:
        "Controls the width of the label associated with the checkbox."
    },
    hint: {
      type: String,
      default: null,
      description:
        "Provides descriptive text to understand the purpose of the checkbox."
    },
    help: {
      type: Object,
      default: null,
      description: "Displays help topic."
    },
    dense: {
      type: Boolean,
      description: "Puts 'dense' css class on the checkbox container"
    },
    displayOptional: {
      type: Boolean,
      default: true,
      description: "Property to override the injected showLabelOptional flag."
    },
    displayRequired: {
      type: Boolean,
      default: true,
      description: "Property to override the injected showLabelRequired flag."
    }
  },
  data: function () {
    return {
      sel: this.selected,
      ariaDescribedByContent: () => {
        if (this.ariaDescribedby) {
          return this.ariaDescribedby;
        }
        return this.hint ? this.id + "_hint" : undefined;
      }
    };
  },

  computed: {
    _fs_class: function () {
      var css = {
        fieldset: true,
        "cat-cb": true,
        "readonly-list": this.readonly,
        invalid: this.errors && this.errors.length > 0,
        required: this.mandatory,
        optional: !this.mandatory,
        dense: this.dense
      };
      css[this.size] = true;
      return css;
    }
  },

  watch: {
    selected: function (sel) {
      this.sel = sel;
    }
  },
  methods: {
    _onChange: function (event) {
      // console.log("check", event.target.checked, this.sel);
      this.$emit("ui-change", {
        event: event,
        id: this.id ? this.id : null,
        value: this.sel,
        code: this.value,
        el: this.$el
      });
    },
    _onFocus: function (event) {
      this.$emit("ui-focus", {
        event: event,
        id: this.id ? this.id : null,
        value: this.sel,
        code: this.value,
        el: this.$el
      });
    },
    _onKeyDown: function (event) {
      //console.log("keydown");
      this.$emit("ui-key-down", {
        event: event,
        id: this.id ? this.id : null,
        value: this.sel,
        el: this.$el
      });
    }
  }
});
</script>
